import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { formatError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
  const [step, setStep] = useState('email'); // email | otp | done
  const [email, setEmail] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      if (data.temp_token) {
        setTempToken(data.temp_token);
        setStep('otp');
        toast.success('OTP sent to your email');
      } else {
        toast.info(data.message);
      }
    } catch (err) { setError(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async () => {
    if (otp.length !== 6) return;
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/auth/reset-password', { temp_token: tempToken, otp_code: otp, new_password: newPassword });
      toast.success(data.message);
      setStep('done');
    } catch (err) { setError(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="forgot-password-page">
      <div className="hidden lg:flex lg:w-1/2 login-bg relative items-end p-12">
        <div className="absolute inset-0 bg-gradient-to-t from-[#163A2E]/80 to-transparent" />
        <div className="relative z-10 text-white max-w-lg">
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-tight mb-4">Quantora Analytics</h1>
          <p className="text-white/80 text-lg">QUANTORA ANALYTICS PRIVATE LIMITED</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-brand-bg">
        <div className="w-full max-w-sm">
          <Link to="/login" className="flex items-center gap-1 text-sm text-brand-text-secondary hover:text-brand-text-primary mb-6" data-testid="back-to-login">
            <ArrowLeft size={16} /> Back to Login
          </Link>

          {step === 'email' && (
            <>
              <div className="mb-8">
                <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Forgot Password</h2>
                <p className="mt-2 text-sm text-brand-text-secondary">Enter your email to receive a password reset OTP</p>
              </div>
              <form onSubmit={handleSendOtp} className="space-y-5">
                {error && <div className="p-3 bg-brand-error/10 border border-brand-error/20 rounded-sm text-brand-error text-sm" data-testid="forgot-error">{error}</div>}
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" required data-testid="forgot-email-input" className="bg-white border-brand-border rounded-sm" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm h-11" data-testid="send-reset-otp-btn">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : 'Send Reset OTP'}
                </Button>
              </form>
            </>
          )}

          {step === 'otp' && (
            <>
              <div className="mb-8">
                <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Reset Password</h2>
                <p className="mt-2 text-sm text-brand-text-secondary">Enter the OTP sent to <strong>{email}</strong> and your new password</p>
              </div>
              {error && <div className="p-3 mb-4 bg-brand-error/10 border border-brand-error/20 rounded-sm text-brand-error text-sm">{error}</div>}
              <div className="space-y-5">
                <div className="flex justify-center">
                  <InputOTP value={otp} onChange={setOtp} maxLength={6} data-testid="reset-otp-input">
                    <InputOTPGroup>
                      <InputOTPSlot index={0} /><InputOTPSlot index={1} /><InputOTPSlot index={2} />
                      <InputOTPSlot index={3} /><InputOTPSlot index={4} /><InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" data-testid="new-password-input" className="bg-white border-brand-border rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label>Confirm Password</Label>
                  <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat password" data-testid="confirm-password-input" className="bg-white border-brand-border rounded-sm" />
                </div>
                <Button onClick={handleResetPassword} disabled={loading || otp.length !== 6} className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm h-11" data-testid="reset-password-btn">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : 'Reset Password'}
                </Button>
              </div>
            </>
          )}

          {step === 'done' && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-brand-success/15 flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-brand-success" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
              <h2 className="font-heading text-2xl font-semibold text-brand-text-primary">Password Reset</h2>
              <p className="text-sm text-brand-text-secondary">Your password has been reset successfully.</p>
              <Button onClick={() => navigate('/login')} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="go-to-login-btn">
                Go to Login
              </Button>
            </div>
          )}

          <p className="mt-8 text-center text-xs text-brand-text-secondary">QUANTORA ANALYTICS PRIVATE LIMITED | quantora.co.in</p>
        </div>
      </div>
    </div>
  );
}
